import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, getErrorMessage } from "@/lib/api";
import { exportTravelBonusPDF } from "@/lib/travelBonusPdf";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, AlertCircle, FileDown } from "lucide-react";

const ZONE_LABELS = {
  1: "Zone 1 (0-10 km)",
  2: "Zone 2 (10-20 km)",
  3: "Zone 3 (20-30 km)",
  4: "Zone 4 (30-40 km)",
  5: "Zone 5 (40-50 km)"
};
const MONTHS_FR = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const EmployeeTravelBonusTab = ({ employeeId }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [viewMode, setViewMode] = useState("month"); // month | year
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasHQ, setHasHQ] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { year, employee_id: employeeId };
      if (viewMode === "month") params.month = month;
      const res = await api.computeTravelBonuses(params);
      setData(res.data);
      setHasHQ(true);
    } catch (e) {
      if (e.response?.status === 400) {
        setHasHQ(false);
      } else {
        toast.error(getErrorMessage(e, "Erreur"));
      }
    } finally { setLoading(false); }
  }, [employeeId, year, month, viewMode]);

  useEffect(() => { load(); }, [load]);

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 3 + i);
  const emp = data?.by_employee?.[0];

  return (
    <div className="space-y-4" data-testid="employee-travel-bonus">
      {!hasHQ && (
        <Card className="border-amber-400 bg-amber-50">
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <div>
              <p className="font-medium text-amber-900">Configuration requise</p>
              <p className="text-sm text-amber-800 mt-1">Configurez d'abord le siège social dans l'onglet "Primes de petit déplacement" (liste des salariés).</p>
            </div>
          </CardContent>
        </Card>
      )}

      {hasHQ && (
        <>
          <Card>
            <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
              <div>
                <Label className="text-xs">Période</Label>
                <Select value={viewMode} onValueChange={setViewMode}>
                  <SelectTrigger className="w-32" data-testid="emp-tb-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Mensuel</SelectItem>
                    <SelectItem value="year">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Année</Label>
                <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
                  <SelectTrigger className="w-28" data-testid="emp-tb-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {viewMode === "month" && (
                <div>
                  <Label className="text-xs">Mois</Label>
                  <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
                    <SelectTrigger className="w-36" data-testid="emp-tb-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS_FR.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {data?.hq && (
                <div className="text-xs text-muted-foreground ml-2">Siège : {data.hq.city} ({data.hq.postal_code})</div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                disabled={!emp || emp.count === 0}
                onClick={() => {
                  const fname = exportTravelBonusPDF(data, {
                    year,
                    month: viewMode === "month" ? month : undefined,
                    employeeId
                  });
                  if (fname) toast.success("PDF exporté");
                  else toast.error("Aucune donnée à exporter");
                }}
                data-testid="emp-tb-export-pdf"
              >
                <FileDown className="h-4 w-4 mr-1" /> Export PDF
              </Button>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Nb de primes</div><div className="text-2xl font-bold">{emp?.count || 0}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Montant total</div><div className="text-2xl font-bold text-primary">{formatCurrency(emp?.amount || 0)}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Période</div><div className="text-lg font-bold">{viewMode === "month" ? `${MONTHS_FR[month]} ${year}` : `Année ${year}`}</div></CardContent></Card>
              </div>

              {/* Répartition par zone */}
              <Card>
                <CardHeader><CardTitle className="text-base">Répartition par zone</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Zone</TableHead><TableHead className="text-right">Nb primes</TableHead><TableHead className="text-right">Montant</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {[1, 2, 3, 4, 5].map(z => (
                        <TableRow key={z}>
                          <TableCell>{ZONE_LABELS[z]}</TableCell>
                          <TableCell className="text-right font-mono">{emp?.by_zone?.[String(z)]?.count || 0}</TableCell>
                          <TableCell className="text-right font-mono font-bold">{formatCurrency(emp?.by_zone?.[String(z)]?.amount || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Vue annuelle : répartition mensuelle */}
              {viewMode === "year" && emp?.by_month && emp.by_month.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Répartition mensuelle</CardTitle></CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>Mois</TableHead><TableHead className="text-right">Nb primes</TableHead><TableHead className="text-right">Montant</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {emp.by_month.map(m => (
                          <TableRow key={m.month}>
                            <TableCell>{MONTHS_FR[m.month]}</TableCell>
                            <TableCell className="text-right font-mono">{m.count}</TableCell>
                            <TableCell className="text-right font-mono font-bold">{formatCurrency(m.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Détail jour */}
              <Card>
                <CardHeader><CardTitle className="text-base">Détail</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  {(data?.per_day || []).length === 0 ? (
                    <p className="text-center text-muted-foreground py-8 text-sm">Aucune prime sur la période</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Commande</TableHead>
                          <TableHead>Ville chantier</TableHead>
                          <TableHead className="text-right">Distance</TableHead>
                          <TableHead className="text-center">Zone</TableHead>
                          <TableHead className="text-right">Montant</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.per_day.map((d, i) => (
                          <TableRow key={`${d.date}-${i}`}>
                            <TableCell className="font-mono text-xs">{d.date}</TableCell>
                            <TableCell className="font-mono text-xs">{d.order_reference}</TableCell>
                            <TableCell className="text-sm">{d.city}</TableCell>
                            <TableCell className="text-right font-mono">{d.distance_km != null ? `${d.distance_km} km` : "-"}</TableCell>
                            <TableCell className="text-center">
                              {d.zone ? <Badge variant="outline">Zone {d.zone}</Badge> : d.error ? <Badge variant="destructive" className="text-[10px]">{d.error}</Badge> : "-"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold">{formatCurrency(d.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeTravelBonusTab;
